import React from 'react';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../../../utils/dateUtils';
import {
    WHATSAPP_FILE_ICON,
    WHATSAPP_ITEM_KIND,
    WHATSAPP_LOAD_ERROR_COPY,
    WHATSAPP_LOAD_OLDER_COPY,
    WHATSAPP_LOADING_COPY,
    WHATSAPP_MEERA_LABEL,
    WHATSAPP_OLDER_ERROR_COPY,
    WHATSAPP_PARTNER_LABEL,
    WHATSAPP_PERSON_LABEL,
    WHATSAPP_RETRY_COPY,
} from '../../../../../../../../store/globalAskAica/whatsapp/constants';
import { WhatsAppItemKind, WhatsAppTimelineItem } from '../../../../../../../../store/globalAskAica/whatsapp/types';
import WhatsAppMessageText from './WhatsAppMessageText';
import { useWhatsAppChatViewLogic } from './useWhatsAppChatViewLogic';
import styles from './WhatsAppChat.module.scss';

type Presentation = { bubbleClass: string; sender: string; showName?: boolean };
type Perspective = 'owner' | 'meera';

/** Every known kind maps explicitly: there is no default side for an unexpected kind. */
const PRESENTATION: Record<Perspective, Record<WhatsAppItemKind, Presentation>> = {
    owner: {
        [WHATSAPP_ITEM_KIND.IN]: { bubbleClass: styles.BubbleIn, sender: WHATSAPP_PERSON_LABEL },
        [WHATSAPP_ITEM_KIND.FILE]: { bubbleClass: styles.BubbleIn, sender: WHATSAPP_PERSON_LABEL },
        [WHATSAPP_ITEM_KIND.OUT]: { bubbleClass: styles.BubbleOut, sender: WHATSAPP_MEERA_LABEL },
    },
    // Meera is the viewpoint: her messages sit on the right and the partner's, named, on the left.
    meera: {
        [WHATSAPP_ITEM_KIND.IN]: { bubbleClass: styles.BubbleOut, sender: '', showName: true },
        [WHATSAPP_ITEM_KIND.FILE]: { bubbleClass: styles.BubbleOut, sender: '', showName: true },
        [WHATSAPP_ITEM_KIND.OUT]: { bubbleClass: styles.BubbleIn, sender: WHATSAPP_MEERA_LABEL },
    },
};

const ItemBody: React.FC<{ item: WhatsAppTimelineItem }> = ({ item }) => (
    <>
        {item.file && (
            <div className={styles.FileCard} data-testid="whatsapp-file-card">
                <span>{`${WHATSAPP_FILE_ICON} ${item.file.n}. ${item.file.name}`}</span>
                {item.file.mark && <span className={styles.FileMark}>{item.file.mark}</span>}
            </div>
        )}
        {item.text && <WhatsAppMessageText text={item.text} />}
        {item.list && (
            <div className={styles.ListCard} data-testid="whatsapp-list-card">
                <div className={styles.ListTitle}>{item.list.title}</div>
                <ul className={styles.ListRows}>
                    {item.list.rows.map((row, i) => (
                        <li key={`${i}-${row}`}>{row}</li>
                    ))}
                </ul>
            </div>
        )}
    </>
);

/** Read-only WhatsApp conversation: by default the person's messages on the right and Meera's on the left; `meera` flips that and names the person. */
const WhatsAppChatView: React.FC<{ perspective?: Perspective; personLabel?: string }> = ({ perspective = 'owner', personLabel }) => {
    const { whatsApp, containerRef, handleScroll, loadOlder } = useWhatsAppChatViewLogic();
    if (!whatsApp) return null;

    const showLoadOlder = whatsApp.hasMore || whatsApp.olderError;
    let loadOlderLabel = WHATSAPP_LOAD_OLDER_COPY;
    if (whatsApp.isLoadingOlder) loadOlderLabel = WHATSAPP_LOADING_COPY;
    else if (whatsApp.olderError) loadOlderLabel = WHATSAPP_RETRY_COPY;

    return (
        <div className={styles.ChatView} ref={containerRef} onScroll={handleScroll} role="log" data-testid="whatsapp-chat-view">
            <div aria-live="polite" className={styles.LoadOlder}>
                {whatsApp.olderError && (
                    <div className={styles.Status} data-testid="whatsapp-older-error">
                        {WHATSAPP_OLDER_ERROR_COPY}
                    </div>
                )}
                {whatsApp.isLoading && <div className={styles.Status}>{WHATSAPP_LOADING_COPY}</div>}
                {showLoadOlder && (
                    <button type="button" className={styles.LoadOlderBtn} onClick={loadOlder} disabled={whatsApp.isLoadingOlder}>
                        {loadOlderLabel}
                    </button>
                )}
            </div>
            {whatsApp.items.map((item) => {
                const { bubbleClass, sender, showName } = PRESENTATION[perspective][item.kind];
                return (
                    <div key={item.id} className={`${styles.Bubble} ${bubbleClass}`} data-testid={`whatsapp-item-${item.kind}`}>
                        {showName ? (
                            <span className={styles.SenderName} data-testid="whatsapp-sender-name">
                                {personLabel?.trim() || WHATSAPP_PARTNER_LABEL}
                            </span>
                        ) : (
                            <span className={styles.SrOnly}>{sender}</span>
                        )}
                        <ItemBody item={item} />
                        <span className={styles.Timestamp}>{formatDateByFormat(item.at, DATE_FORMATS.LIBRARY_FILE_TIMESTAMP)}</span>
                    </div>
                );
            })}
            {whatsApp.hasError && (
                <div className={styles.Status} role="alert">
                    {WHATSAPP_LOAD_ERROR_COPY}
                </div>
            )}
        </div>
    );
};

export default WhatsAppChatView;
